import PropTypes from 'prop-types'
import { useSearchParams } from 'react-router-dom'

import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useUserContext } from '../../../context'
import { useSelectionParams } from '../hooks/useSelectionParams'
import { getDefaultResult, spotlights as initialSpotlights } from '../utils'

export const DiscoverContext = createContext(undefined)

export const DiscoverContextProvider = ({ children }) => {
  const { activeSpace } = useUserContext()
  const hasCompletedOnboarding = activeSpace?.hasCompletedOnboarding

  const [searchParams, setSearchParams] = useSearchParams()
  const [inviteTalentOpen, setInviteTalentOpen] = useState(false)

  const [addCareerModalOpen, setAddCareerModalOpen] = useState(false)
  const [selectedCareer, setSelectedCareer] = useState(null)
  const [selectedTalent, setSelectedTalent] = useState(null)
  const [runMatchingProfileJoyride, setRunMatchingProfileJoyride] =
    useState(false)
  const [
    runMatchingProfileJoyridePartTwo,
    setRunMatchingProfileJoyridePartTwo
  ] = useState(false)

  const [spotlights, setSpotlights] = useState(initialSpotlights)

  const [
    runMatchingProfileJoyridePartThree,
    setRunMatchingProfileJoyridePartThree
  ] = useState(false)
  const [showAssistantBackButton, setShowAssistantBackButton] = useState(false)
  const [isChancesAndRisksLoading, setIsChancesAndRisksLoading] =
    useState(false)
  const [
    shouldShowCompletionOnboardingModal,
    setShouldShowCompletionOnboardingModal
  ] = useState(false)
  const [shouldClickOnMandatoryDimension, setShouldClickOnMandatoryDimension] =
    useState(false)
  const [showAssistant, setShowAssistant] = useState(true)
  const [assistantIsTyping, setAssistantIsTyping] = useState(false)
  const [assistantDetail, setAssistantDetail] = useState(false)
  const [isFlexibleMatchingEnabled, setIsFlexibleMatchingEnabledState] =
    useState(searchParams.get('flexibleMatching') === 'true')

  const welcomeVideoClosedRef = useRef(false)
  // Welcome video removed from onboarding flow: keep flag false
  const [welcomeVideoOpen, _setWelcomeVideoOpen] = useState(false)

  const [enableClickOnFirstDimension, setEnableClickOnFirstDimension] =
    useState(false)
  const [
    enableClickOnFirstMiddleFitDimension,
    setEnableClickOnFirstMiddleFitDimension
  ] = useState(false)

  // Retain setter for potential future reintroduction; it now only closes.
  const setWelcomeVideoOpen = useCallback((open) => {
    if (welcomeVideoClosedRef.current) return
    if (!open) {
      welcomeVideoClosedRef.current = true
      _setWelcomeVideoOpen(false)
    }
  }, [])

  const [runRequirementProfileJoyride, setRunRequirementProfileJoyride] =
    useState(false)

  const [assistantFocus, setAssistantFocus] = useState(false)
  const [assistantSpotlightSelector, setAssistantSpotlightSelector] =
    useState(null)

  const [assessmentPreviewModalOpen, setAssessmentPreviewModalOpen] =
    useState(false)
  const [completionSummaryModalOpen, setCompletionSummaryModalOpen] =
    useState(false)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const [runDashboardUnlockedJoyride, setRunDashboardUnlockedJoyride] =
    useState(false)
  const [assistantMessages, setAssistantMessages] = useState([])
  const [customIsLoading, setCustomIsLoading] = useState(null)

  const [assistantVideoMuted, setAssistantVideoMuted] = useState(true)

  const { talents: selectedTalentIds, careers: selectedCareerIds } =
    useSelectionParams()

  // Direkt beim ersten Render aus der URL ableiten. Mit null als Startwert
  // rendert das ProfileDropdown einen Frame lang ohne Profil, bevor der
  // Effect unten greift.
  const [selectedResult, setSelectedResult] = useState(() =>
    getDefaultResult(selectedTalentIds, selectedCareerIds)
  )

  const setIsFlexibleMatchingEnabled = useCallback(
    (enabled) => {
      setIsFlexibleMatchingEnabledState(enabled)

      const newParams = new URLSearchParams(searchParams)
      if (enabled) {
        newParams.set('flexibleMatching', 'true')
      } else {
        newParams.delete('flexibleMatching')
      }
      setSearchParams(newParams, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  // Anforderung der Stelle im Teamprofil. Wie bei flexibleMatching steht nur
  // die Abweichung vom Standard in der URL, direkt aus searchParams gelesen.
  // Sie bleibt beim Stellen- und Profilwechsel erhalten.
  const isRequirementOverlayHidden =
    searchParams.get('requirement') === 'hidden'

  const setIsRequirementOverlayHidden = useCallback(
    (hidden) => {
      const newParams = new URLSearchParams(searchParams)
      if (hidden) {
        newParams.set('requirement', 'hidden')
      } else {
        newParams.delete('requirement')
      }
      setSearchParams(newParams, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  const toggleSpotlight = (key, isOpen) =>
    setSpotlights((prev) => ({ ...prev, [key]: isOpen ?? !prev[key] }))

  useEffect(() => {
    const flexibleMatchingParam = searchParams.get('flexibleMatching')
    setIsFlexibleMatchingEnabledState(flexibleMatchingParam === 'true')
  }, [searchParams])

  useEffect(() => {
    const defaultResult = getDefaultResult(selectedTalentIds, selectedCareerIds)
    if (defaultResult) setSelectedResult(defaultResult)
  }, [selectedTalentIds, selectedCareerIds])

  // useEffect(() => {
  //   if (welcomeVideoOpen === false) {
  //     setTimeout(() => {
  //       setShowAssistant(true)
  //     }, 3000)
  //   }
  // }, [welcomeVideoOpen])

  /**
   * Adds a new message object to the assistant's message list.
   *
   * @param {Object} message - The message object to add.
   * @param {string|number} message.id - Unique identifier for the message.
   * @param {string} message.text - The content of the message.
   * @param {string} message.role - The role of the sender (e.g., 'user', 'assistant').
   * @param {string} message.type - The type of the message (e.g., 'text', 'image').
   */
  const sendMessageToAssistant = useCallback((message) => {
    setAssistantMessages((prevMessages) => {
      if (!message?.id) return [...prevMessages, message]

      const dedupedMessages = prevMessages.filter(
        (prevMessage) => prevMessage.id !== message.id
      )

      return [...dedupedMessages, message]
    })
  }, [])

  const clearAssistantMessages = useCallback(() => {
    setAssistantMessages([])
  }, [])

  const hideAssistant = useCallback(() => {
    clearAssistantMessages()
    setShowAssistant(false)
    setAssistantFocus(false)
    setAssistantDetail(false)
  }, [clearAssistantMessages])

  const enableAssistant = useCallback(() => {
    clearAssistantMessages()
    setShowAssistant(true)

    setAssistantFocus(true)
  }, [clearAssistantMessages])

  const value = useMemo(
    () => ({
      selectedCareer,
      setSelectedCareer,
      selectedTalent,
      setSelectedTalent,
      selectedResult,
      setSelectedResult,
      showAssistant,
      setShowAssistant,
      welcomeVideoOpen,
      setWelcomeVideoOpen,
      assistantMessages,
      assistantVideoMuted,
      setAssistantVideoMuted,
      sendMessageToAssistant,
      clearAssistantMessages,
      hideAssistant,
      enableAssistant,
      assistantIsTyping,
      setAssistantIsTyping,
      assistantDetail,
      setAssistantDetail,
      isFlexibleMatchingEnabled,
      setIsFlexibleMatchingEnabled,
      isRequirementOverlayHidden,
      setIsRequirementOverlayHidden,
      inviteTalentOpen,
      setInviteTalentOpen,
      addCareerModalOpen,
      setAddCareerModalOpen,
      assistantFocus,
      setAssistantFocus,
      assistantSpotlightSelector,
      setAssistantSpotlightSelector,
      customIsLoading,
      setCustomIsLoading,
      hasCompletedOnboarding,
      isChancesAndRisksLoading,
      setIsChancesAndRisksLoading,
      shouldClickOnMandatoryDimension,
      setShouldClickOnMandatoryDimension,
      showAssistantBackButton,
      setShowAssistantBackButton,
      shouldShowCompletionOnboardingModal,
      setShouldShowCompletionOnboardingModal,
      runMatchingProfileJoyride,
      setRunMatchingProfileJoyride,
      runMatchingProfileJoyridePartTwo,
      setRunMatchingProfileJoyridePartTwo,
      runMatchingProfileJoyridePartThree,
      setRunMatchingProfileJoyridePartThree,
      assessmentPreviewModalOpen,
      setAssessmentPreviewModalOpen,
      completionSummaryModalOpen,
      setCompletionSummaryModalOpen,
      showFeedbackModal,
      setShowFeedbackModal,
      runDashboardUnlockedJoyride,
      setRunDashboardUnlockedJoyride,
      runRequirementProfileJoyride,
      setRunRequirementProfileJoyride,
      enableClickOnFirstDimension,
      setEnableClickOnFirstDimension,
      enableClickOnFirstMiddleFitDimension,
      setEnableClickOnFirstMiddleFitDimension,
      spotlights,
      toggleSpotlight
    }),
    [
      selectedCareer,
      selectedTalent,
      selectedResult,
      showAssistant,
      welcomeVideoOpen,
      setWelcomeVideoOpen,
      assistantMessages,
      assistantVideoMuted,
      sendMessageToAssistant,
      clearAssistantMessages,
      hideAssistant,
      enableAssistant,
      assistantIsTyping,
      assistantDetail,
      isFlexibleMatchingEnabled,
      setIsFlexibleMatchingEnabled,
      isRequirementOverlayHidden,
      setIsRequirementOverlayHidden,
      inviteTalentOpen,
      addCareerModalOpen,
      assistantFocus,
      assistantSpotlightSelector,
      customIsLoading,
      hasCompletedOnboarding,
      isChancesAndRisksLoading,
      shouldClickOnMandatoryDimension,
      showAssistantBackButton,
      shouldShowCompletionOnboardingModal,
      runMatchingProfileJoyride,
      setRunMatchingProfileJoyride,
      runMatchingProfileJoyridePartTwo,
      setRunMatchingProfileJoyridePartTwo,
      runMatchingProfileJoyridePartThree,
      setRunMatchingProfileJoyridePartThree,
      assessmentPreviewModalOpen,
      completionSummaryModalOpen,
      showFeedbackModal,
      setShowFeedbackModal,
      runDashboardUnlockedJoyride,
      runRequirementProfileJoyride,
      enableClickOnFirstDimension,
      enableClickOnFirstMiddleFitDimension,
      spotlights
    ]
  )

  return (
    <DiscoverContext.Provider value={value}>
      {children}
    </DiscoverContext.Provider>
  )
}

DiscoverContextProvider.propTypes = {
  children: PropTypes.node
}
